import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { Wrench, Ban, Coffee } from 'lucide-react';
import { MaintenancePeriod } from './types';

interface MaintenanceBlockProps {
  maintenance: MaintenancePeriod;
  left: number;
  width: number;
  height: number;
}

export function MaintenanceBlock({ maintenance, left, width, height }: MaintenanceBlockProps) {
  const getMaintenanceStyle = () => {
    switch (maintenance.type) {
      case 'maintenance':
        return {
          bgcolor: '#9ca3af',
          borderColor: '#4b5563',
          pattern: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 10px, transparent 10px, transparent 20px)',
          icon: <Wrench style={{ width: 12, height: 12, color: '#ffffff' }} />
        };
      case 'unavailable':
        return {
          bgcolor: '#f87171',
          borderColor: '#dc2626',
          pattern: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 10px, transparent 10px, transparent 20px)',
          icon: <Ban style={{ width: 12, height: 12, color: '#ffffff' }} />
        };
      case 'break':
        return {
          bgcolor: '#d4d4d4',
          borderColor: '#737373',
          pattern: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15), rgba(255,255,255,0.15) 10px, transparent 10px, transparent 20px)',
          icon: <Coffee style={{ width: 12, height: 12, color: '#ffffff' }} />
        };
    }
  };

  const style = getMaintenanceStyle();
  
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {style.icon}
        <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {maintenance.type === 'maintenance' ? 'Maintenance Period' :
           maintenance.type === 'unavailable' ? 'Vehicle Unavailable' :
           'Break Time'}
        </Box>
      </Box>
      <Box sx={{ fontSize: '0.75rem' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 500 }}>Reason:</Box>
          <Box component="span">{maintenance.reason}</Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 500 }}>Start:</Box>
          <Box component="span">{formatDate(maintenance.startTime)} {formatTime(maintenance.startTime)}</Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box component="span" sx={{ fontWeight: 500 }}>End:</Box>
          <Box component="span">{formatDate(maintenance.endTime)} {formatTime(maintenance.endTime)}</Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          left,
          width,
          height,
          bgcolor: style.bgcolor,
          border: `2px solid ${style.borderColor}`,
          borderRadius: 1.5,
          boxShadow: 1,
          cursor: 'pointer',
          overflow: 'hidden',
          backgroundImage: style.pattern
        }}
      >
        <Box sx={{ p: 0.75, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', zIndex: 10 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {style.icon}
            <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {maintenance.type === 'maintenance' ? 'Maintenance' :
               maintenance.type === 'unavailable' ? 'Unavailable' :
               'Break'}
            </Box>
          </Box>
          
          {width > 80 && (
            <Box sx={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {maintenance.reason}
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}
